import React from "react";
import { Box, Container, Grid, Stack, Typography, alpha } from "@mui/material";
import {
  CampaignOutlined,
  ContentCutOutlined,
  AutoStoriesOutlined,
  Face2Outlined,
  VideocamOutlined,
  ScheduleSendOutlined,
} from "@mui/icons-material";
import { useCustomTheme } from "../../utils/useCustomTheme";
import { TranslateContext } from "../../context/TranslateContext";

const ICONS = {
  clipping: ContentCutOutlined,
  stories: AutoStoriesOutlined,
  ads: CampaignOutlined,
  characters: Face2Outlined,
  studio: VideocamOutlined,
  social: ScheduleSendOutlined,
};

function FeatureTile({ iconKey, title, description, accent }) {
  const { config, isDark } = useCustomTheme();
  const Icon = ICONS[iconKey] || VideocamOutlined;
  const border = isDark ? config.border_dark : config.border_light;
  const paper = isDark ? config.background_paper_dark : config.background_paper_light;

  return (
    <Box
      sx={{
        height: "100%",
        p: 3,
        borderRadius: 3,
        border: `1px solid ${border}`,
        bgcolor: paper,
        transition: "transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease",
        "&:hover": {
          transform: "translateY(-6px)",
          boxShadow: isDark ? "0 24px 48px rgba(0,0,0,0.45)" : "0 24px 48px rgba(0,0,0,0.1)",
          borderColor: alpha(accent, 0.45),
        },
      }}
    >
      <Box
        sx={{
          width: 52,
          height: 52,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 2,
          background: `linear-gradient(135deg, ${alpha(accent, 0.2)} 0%, ${alpha(accent, 0.05)} 100%)`,
          border: `1px solid ${alpha(accent, 0.25)}`,
        }}
      >
        <Icon sx={{ color: accent, fontSize: 28 }} />
      </Box>
      <Typography variant="h6" fontWeight={700} gutterBottom sx={{ fontFamily: config.font_family }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65, fontFamily: config.font_family }}>
        {description}
      </Typography>
    </Box>
  );
}

export default function FeaturesSection() {
  const { lang } = React.useContext(TranslateContext);
  const { config, isDark } = useCustomTheme();
  const accent = isDark ? config.primary_dark || config.primary : config.primary;

  const features = [
    {
      icon: "clipping",
      title: lang?.homeFeatureClippingTitle || "AI Clipping",
      desc:
        lang?.homeFeatureClippingDesc ||
        "Auto-cut long videos into captioned Shorts. Monetize through theme pages, creator funds, and clipping-as-a-service.",
    },
    {
      icon: "stories",
      title: lang?.homeFeatureStoriesTitle || "AI Stories",
      desc:
        lang?.homeFeatureStoriesDesc ||
        "Multi-scene narrative videos with voiceover — perfect for faceless YouTube, edu-tainment, and story channels.",
    },
    {
      icon: "ads",
      title: lang?.homeFeatureAdsTitle || "Converting ads",
      desc:
        lang?.homeFeatureAdsDesc ||
        "UGC product ads with AI spokespeople, real product shots, and scroll-stopping hooks built to drive sales.",
    },
    {
      icon: "characters",
      title: lang?.homeFeatureCharactersTitle || "AI Characters",
      desc:
        lang?.homeFeatureCharactersDesc ||
        "Consistent digital creators for every niche — same face across ads, clips, and story content.",
    },
    {
      icon: "studio",
      title: lang?.homeFeatureStudioTitle || "Video Studio",
      desc:
        lang?.homeFeatureStudioDesc ||
        "Text-to-video, image-to-video, motion sync, and music-driven visuals with Kling and top models.",
    },
    {
      icon: "social",
      title: lang?.homeFeatureSocialTitle || "Publish everywhere",
      desc:
        lang?.homeFeatureSocialDesc ||
        "Schedule posts to TikTok, Instagram, YouTube, and Facebook — clips and ads saved to your library.",
    },
  ];

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 5, md: 7 },
        bgcolor: isDark ? config.background_default_dark : config.background_default_light,
      }}
    >
      <Container maxWidth="lg">
        <Stack spacing={1.5} textAlign="center" mb={6} maxWidth={640} mx="auto">
          <Typography
            variant="overline"
            fontWeight={700}
            letterSpacing="0.12em"
            color="text.secondary"
          >
            {lang?.homeFeaturesBadge || "Platform"}
          </Typography>
          <Typography
            component="h2"
            variant="h3"
            fontWeight={800}
            letterSpacing="-0.03em"
            sx={{ fontFamily: config.font_family }}
          >
            {lang?.homeFeaturesTitle || "Everything you need to"}{" "}
            <Box component="span" sx={{ color: accent }}>
              {lang?.homeFeaturesTitleAccent || "create at scale"}
            </Box>
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            {lang?.homeFeaturesSubtitle ||
              "Clip for income, tell AI stories, run ads that convert, and publish everywhere — one platform for creators and brands."}
          </Typography>
        </Stack>

        <Grid container spacing={2.5}>
          {features.map((f) => (
            <Grid item xs={12} sm={6} md={4} key={f.icon}>
              <FeatureTile iconKey={f.icon} title={f.title} description={f.desc} accent={accent} />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
